import { useEffect, useState } from 'react';
import { ArrowRight, Shield, Clock, Zap, TrendingUp, Users, Globe } from 'lucide-react';
import ExchangeWidget from './ExchangeWidget';
import './Hero.css';

const Hero = () => {
  const [stats, setStats] = useState([
    { value: 0, target: 1000, suffix: '+', label: 'Supported Coins' },
    { value: 0, target: 500000, suffix: '+', label: 'Happy Users' },
    { value: 0, target: 2, suffix: 'B+', label: 'USD Exchanged', prefix: '$' },
    { value: 0, target: 4.9, suffix: '/5', label: 'Trust Score' },
  ]);

  // Animate stats
  useEffect(() => {
    const duration = 2000;
    const steps = 60;
    const interval = duration / steps;
    let step = 0;

    const timer = setInterval(() => {
      step++;
      const progress = step / steps;
      const easeOut = 1 - Math.pow(1 - progress, 3);

      setStats(prev => prev.map(stat => ({
        ...stat,
        value: stat.target < 10 
          ? parseFloat((stat.target * easeOut).toFixed(1))
          : Math.floor(stat.target * easeOut)
      })));

      if (step >= steps) {
        clearInterval(timer);
      }
    }, interval);

    return () => clearInterval(timer);
  }, []);

  const features = [
    { icon: Shield, text: 'Non-custodial' },
    { icon: Clock, text: '2 min average' },
    { icon: Zap, text: 'No limits' },
    { icon: TrendingUp, text: 'Best rates' },
  ];

  return (
    <section className="hero" id="exchange">
      {/* Background Effects */}
      <div className="hero-bg">
        <div className="gradient-orb orb-1" />
        <div className="gradient-orb orb-2" />
        <div className="gradient-orb orb-3" />
        <div className="grid-pattern" />
      </div>

      <div className="hero-container">
        {/* Left Content */}
        <div className="hero-content">
          <div className="hero-badge">
            <span className="pulse-dot" />
            <span>Live Exchange Rates</span>
          </div>

          <h1 className="hero-title">
            Limitless{' '}
            <span className="gradient-text">Crypto Exchange</span>
          </h1>

          <p className="hero-description">
            Fast, private, and non-custodial exchange. Swap 1000+ cryptocurrencies 
            instantly with no registration required.
          </p>

          <div className="hero-features">
            {features.map((feature, index) => (
              <div key={index} className="hero-feature">
                <feature.icon className="feature-icon" />
                <span>{feature.text}</span>
              </div>
            ))}
          </div>

          <div className="hero-cta">
            <a href="#how-it-works" className="cta-link">
              How it works
              <ArrowRight className="cta-arrow" />
            </a>
          </div>

          {/* Stats */}
          <div className="hero-stats">
            {stats.map((stat, index) => (
              <div key={index} className="stat-item">
                <div className="stat-value">
                  {stat.prefix}{stat.value.toLocaleString()}{stat.suffix}
                </div>
                <div className="stat-label">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Right Content - Exchange Widget */}
        <div className="hero-widget">
          <ExchangeWidget />
        </div>
      </div>

      {/* Trust Bar */}
      <div className="trust-bar">
        <div className="trust-item">
          <Shield className="trust-icon" />
          <span>Non-custodial</span>
        </div>
        <div className="trust-divider" />
        <div className="trust-item">
          <Clock className="trust-icon" />
          <span>Average 2 min</span>
        </div>
        <div className="trust-divider" />
        <div className="trust-item">
          <Globe className="trust-icon" />
          <span>Global coverage</span>
        </div>
        <div className="trust-divider" />
        <div className="trust-item">
          <Users className="trust-icon" />
          <span>500K+ users</span>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="scroll-indicator">
        <div className="mouse">
          <div className="wheel" />
        </div>
        <span>Scroll to explore</span>
      </div>
    </section>
  );
};

export default Hero;
